import React, { useState } from "react";
import {
  BookOpen,
  Briefcase,
  FileText,
  Wrench,
  Crosshair,
  Layers,
  Mail,
  Loader2,
  ArrowUpRight,
  Instagram,
} from "lucide-react";
import { sendMagicLink } from "@/lib/api";
import SEO from "@/components/SEO";

/* All door clicks route through /r/redirect so the proxy can track them. */
const REDIRECT = (to, e) => `https://jobhacki.com/r/redirect?to=${to}&e=${e}`;

const DOORS = [
  { e: "playbooks", to: "/playbooks", label: "100+ Money-Making Playbooks", Icon: BookOpen, dot: "#FFE600" },
  { e: "jobs", to: "/jobs", label: "The #1 Job Board to Land a Job", Icon: Briefcase, dot: "#0055FF" },
  { e: "resume", to: "/resume", label: "The #1 Resume Builder", Icon: FileText, dot: "#FF3B30" },
  { e: "toolbox", to: "/toolbox", label: "100+ GitHub Repos, AI Prompts & Tools", Icon: Wrench, dot: "#0a0a0a" },
  { e: "rolefit", to: "/simulator", label: "Job-Readiness Test (Any Job)", Icon: Crosshair, dot: "#FF00FF" },
  { e: "allaccess", to: "/dashboard", label: "Get All the Above — Free", Icon: Layers, dot: "#00FF66" },
];

function PillLink({ door }) {
  const { Icon } = door;
  return (
    <a
      href={REDIRECT(door.to, door.e)}
      data-testid={`door-${door.e}`}
      data-event={door.e}
      className="group block w-full bg-white border-4 border-[#0a0a0a] hover:bg-[#FFE600] active:translate-x-[2px] active:translate-y-[2px] shadow-brutal-sm hover:shadow-brutal transition-all"
    >
      <div className="flex items-center gap-3 px-5 py-4">
        <span
          className="w-2.5 h-2.5 rounded-full border-2 border-[#0a0a0a]"
          style={{ background: door.dot }}
        />
        <Icon size={18} strokeWidth={2.5} className="shrink-0" />
        <span className="flex-1 text-left font-display text-[15px] md:text-base leading-tight">
          {door.label}
        </span>
        <ArrowUpRight
          size={16}
          strokeWidth={3}
          className="shrink-0 text-[#0a0a0a]/40 group-hover:text-[#0a0a0a] transition-colors"
        />
      </div>
    </a>
  );
}

export default function GetPlaybook() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      setErr("That doesn't look like an email.");
      return;
    }
    setErr("");
    setSubmitting(true);
    try {
      await sendMagicLink(email);
      setSent(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="get-playbook"
      className="min-h-screen bg-[#FDFBF7] text-[#0a0a0a] bg-grid flex flex-col"
    >
      <SEO
        title="Pick your path — JobHacki"
        description="Everything we share — free. Tap any door, or drop your email to get them all in one place."
        canonical="https://jobhacki.com/get/playbook"
      />

      <main className="flex-1 flex flex-col items-center px-5 pt-12 md:pt-16 pb-10">
        <div className="w-full max-w-[420px] flex flex-col items-center">
          {/* Avatar + handle */}
          <a
            href="/"
            data-testid="gp-logo"
            className="block"
          >
            <img
              src="/jobhacki-logo.png"
              alt="JobHacki"
              className="w-20 h-20 border-4 border-[#0a0a0a] rounded-2xl object-cover shadow-brutal"
            />
          </a>
          <h1
            data-testid="gp-title"
            className="font-display text-2xl md:text-3xl mt-5"
          >
            @jobhacki
          </h1>
          <a
            href="https://instagram.com/jobhacki"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 font-mono-body text-[12px] text-[#0a0a0a]/60 mt-1 hover:text-[#0a0a0a]"
          >
            <Instagram size={11} strokeWidth={2.5} />
            instagram.com/jobhacki
          </a>
          <p className="font-mono-body text-[13px] md:text-sm text-[#0a0a0a]/70 mt-3 text-center max-w-[340px]">
            Pick your path — everything we share, free.
          </p>

          {/* Pill links */}
          <div
            data-testid="gp-doors"
            className="w-full flex flex-col gap-2.5 mt-7"
          >
            {DOORS.map((d) => (
              <PillLink key={d.e} door={d} />
            ))}
          </div>

          {/* Tiny email gate */}
          <section
            data-testid="gp-capture"
            className="w-full mt-8"
          >
            {sent ? (
              <div
                data-testid="gp-sent"
                className="bg-[#0a0a0a] text-[#FFE600] border-4 border-[#0a0a0a] shadow-brutal-sm px-5 py-4 text-center"
              >
                <div className="font-display text-base md:text-lg">
                  Check your inbox.
                </div>
                <div className="font-mono-body text-[11px] mt-1 text-[#FFE600]/70 break-all">
                  Link sent to {email}
                </div>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="gp-form"
                className="w-full"
              >
                <div className="flex items-stretch gap-0 border-4 border-[#0a0a0a] bg-white shadow-brutal-sm">
                  <input
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    aria-label="Email"
                    data-testid="gp-email"
                    className="flex-1 min-w-0 px-4 py-3 font-mono-body text-sm outline-none bg-white"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="gp-submit"
                    className="bg-[#0055FF] hover:bg-[#0a0a0a] text-white border-l-4 border-[#0a0a0a] px-4 font-accent text-xs inline-flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-wait transition-colors"
                  >
                    {submitting ? (
                      <Loader2 size={13} className="animate-spin" />
                    ) : (
                      <>
                        Get all <Mail size={12} strokeWidth={3} />
                      </>
                    )}
                  </button>
                </div>
                {err && (
                  <div
                    data-testid="gp-error"
                    className="font-accent text-[10px] text-[#FF3B30] mt-2 text-center"
                  >
                    {err}
                  </div>
                )}
                <p className="font-mono-body text-[10.5px] text-[#0a0a0a]/55 mt-3 text-center leading-relaxed">
                  Free account · one-time magic link · no password.
                </p>
              </form>
            )}
          </section>
        </div>
      </main>

      <footer className="border-t-2 border-[#0a0a0a]/10">
        <div className="max-w-[420px] mx-auto px-5 py-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 font-mono-body text-[10.5px] text-[#0a0a0a]/55">
          <span>© {new Date().getFullYear()} JobHacki</span>
          <a href="/privacy" className="hover:underline">Privacy</a>
          <a href="/terms" className="hover:underline">Terms</a>
          <a href="/disclaimer" className="hover:underline">Disclaimer</a>
          <a href="/login" className="hover:underline">Log in</a>
        </div>
      </footer>
    </div>
  );
}
